import React, { useState, useEffect, lazy, Suspense, useRef } from "react";
import { useTranslation } from "react-i18next";
import scriptLoader from "../../../utils/javascriptsLoader";
import "react-phone-number-input/style.css";
import ReactGA from "react-ga4";
import ConnectNowModel from "../../whatsappBot/ConnectNowModel";
import style from "../../style/home.module.css";
import "./Home.css";
import Clientele from "../AboutSection/Clientele"; // Always visible

const LazyHireExperts = lazy(() =>
  import("../HireExExpertSection/HireExExperts")
);
const LazyTestimonial = lazy(() => import("../TestimonySection/Testimonial"));
const LazyLatestArticle = lazy(() =>
  import("../LatestArticleSection/LatestArticle")
);

const Home = () => {
  const { t } = useTranslation();
  const [currentScreenWidth, setCurrentScreenWidth] = useState(
    window.innerWidth
  );
  const [showVideo, setShowVideo] = useState(false);
  const [showHireExperts, setShowHireExperts] = useState(false);
  const [showTestimonial, setShowTestimonial] = useState(false);
  const [showLatestArticle, setShowLatestArticle] = useState(false);

  const hireExpertsRef = useRef(null);
  const testimonialRef = useRef(null);
  const latestArticleRef = useRef(null);

  useEffect(() => {
    loadMainScript();
    window.addEventListener("resize", updateScreenWidth);

    const checkDocumentReady = () => {
      if (document.readyState === "complete") {
        setTimeout(() => setShowVideo(true), 2000);
      } else {
        const onLoad = () => {
          setTimeout(() => setShowVideo(true), 3000);
          window.removeEventListener("load", onLoad);
        };
        window.addEventListener("load", onLoad);
      }
    };

    checkDocumentReady();
    observeSections();

    return () => {
      window.removeEventListener("resize", updateScreenWidth);
    };
  }, []);

  const observeSections = () => {
    const observerOptions = { threshold: 0.2 };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (entry.target === hireExpertsRef.current) setShowHireExperts(true);
          if (entry.target === testimonialRef.current) setShowTestimonial(true);
          if (entry.target === latestArticleRef.current)
            setShowLatestArticle(true);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    if (hireExpertsRef.current) observer.observe(hireExpertsRef.current);
    if (testimonialRef.current) observer.observe(testimonialRef.current);
    if (latestArticleRef.current) observer.observe(latestArticleRef.current);
  };

  const loadMainScript = () => {
    loadLazyJavaScript();
    loadScript();
    loadSiteTrackingScripts();
  };

  const loadScript = () => {
    const scriptParam = {
      id: "usageScriptId",
      src: process.env.PUBLIC_URL + "/js/modules/script.js",
      async: 1,
      defer: 1,
    };
    scriptLoader(scriptParam);
  };

  const loadSiteTrackingScripts = () => {
    ReactGA.initialize([
      { trackingId: "G-TPKZRWHSGV" },
      { trackingId: "AW-995102072" },
    ]);
    ReactGA.send({ hitType: "pageview", page: "/", title: "Home Page View" });
  };

  const loadLazyJavaScript = () => {
    if (!document.getElementById("lazyLoadScriptId")) {
      const script = {
        src: process.env.REACT_APP_BASE_URL + "js/lazyload.min.js",
        id: "lazyLoadScriptId",
        async: 1,
        defer: 1,
      };
      scriptLoader(script, () => {
        if (window.lazyLoadInstance) {
          window.lazyLoadInstance.update();
        }
      });
    }
  };

  const updateScreenWidth = () => {
    setCurrentScreenWidth(window.innerWidth);
  };

  const mobileWidth = currentScreenWidth >= 500;

  return (
    <>
      <section id="home" className="cursor-light p-0">
        <div className={style.backgroundVideoContainer}>
          <div className={style.bgOverlay}></div>
          {!showVideo ? (
            <img
              src={
                mobileWidth ? "images/bgDesktop.webp" : "images/bgMobile.webp"
              }
              alt="Background"
              loading="lazy"
            />
          ) : (
            <video
              src={
                mobileWidth ? "videos/bgDesktop.webm" : "videos/bgMobile.webm"
              }
              autoPlay
              loop
              muted
              playsInline
              loading="lazy"
            ></video>
          )}
        </div>
        <div className="fullscreen-container">
          <div className="z-index-1">
            <div className="landing-main">
              <div className="col-md-6 landing-div2">
                <div className="landing-div1">
                  <h1 className="text-white">
                    <span className="home-title">{t("home.title")} </span>
                    {t("home.datascience")}
                  </h1>
                </div>
                <p className="landing-des text-white">
                   {t("home.description3")}
                    <br /> <br />
                  {t("home.description1")}
                  <br /> <br />
                  {t("home.description2")}
                </p>
                <ConnectNowModel />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Clientele />

      <div ref={hireExpertsRef}>
        {showHireExperts && (
          <Suspense fallback={<div>Loading Hire Experts...</div>}>
            <LazyHireExperts />
          </Suspense>
        )}
      </div>

      <div ref={testimonialRef} className="home-section-spacer">
        {showTestimonial && (
          <Suspense fallback={<div>Loading Testimonials...</div>}>
            <LazyTestimonial />
          </Suspense>
        )}
      </div>

      <div ref={latestArticleRef} className="home-section-spacer">
        {showLatestArticle && (
          <Suspense fallback={<div>Loading Latest Articles...</div>}>
            <LazyLatestArticle />
          </Suspense>
        )}
      </div>
    </>
  );
};

export default Home;
